import React, { useCallback, useState } from 'react';
import { Account, ErrorWithMessage, useMe } from '@app/api';
import { LoginViaCodeModal } from './LoginViaCodeModal';
import { useTranslation } from 'react-i18next';

const LoginParticipantContext = React.createContext<
  ((participantId: string) => Promise<Account | void>) | undefined
>(undefined);

interface Props {
  children: React.ReactNode;
}
export const LoginParticipantContextProvider = ({ children }: Props) => {
  const { data: me } = useMe(true);
  const { t } = useTranslation('common');

  const [state, setState] = useState<
    | {
        participantId: string;
        resolve: (account: Account) => void;
        reject: (err: Error) => void;
      }
    | undefined
  >(undefined);

  const close = useCallback(() => {
    if (!state) {
      return;
    }
    state.reject(new ErrorWithMessage(t('errors.OPERATION_CANCELLED')));
    setState(undefined);
  }, [state, setState, t]);

  const onSuccess = useCallback(
    (account: Account) => {
      if (!state) {
        return;
      }
      state.resolve(account);
      setState(undefined);
    },
    [state, setState],
  );

  const login = useCallback(
    async (participantId: string) => {
      if (me) {
        return Promise.resolve();
      }
      return new Promise<Account>((resolve, reject) => {
        setState({
          participantId,
          resolve,
          reject,
        });
      });
    },
    [setState, me],
  );

  return (
    <>
      <LoginParticipantContext.Provider value={login}>
        {children}
      </LoginParticipantContext.Provider>
      <LoginViaCodeModal
        isOpen={!!state}
        onRequestClose={close}
        onSuccess={onSuccess}
        participantId={state?.participantId}
      />
    </>
  );
};

export const useLoginParticipantWithModal = () => {
  const login = React.useContext(LoginParticipantContext);
  if (!login) {
    console.warn(
      'useLoginParticipantWithModal must be used within LoginContextProvider',
    );
    return async (_participantId: string) => {};
  }
  return login;
};
